import React, { useEffect, useState } from 'react';
import removeMd from 'remove-markdown';
import { Layout, routes, pageTypes, siteName, AdBox, AdMobileAnchor, AdMobileBanner, AdLeaderboard, AdVideo } from 'gatsby-theme-apollo';
import Footer from '../templates/page/footer';
import GameContent from '../templates/page/content';
import ContentFeed from '../templates/feeds/content-feed';
import Section from '../templates/layout/section';
import { ResponsiveImage, HeaderImage } from '../templates/media/responsive-image';
import { MainColumn, TwoColumn, SecondaryColumn } from '../templates/layout/columns';
import Post from '../templates/posts/post';
import { getPostByPermalink } from '../hooks/use-post';
import LoadingIndicator from '../templates/status/status-message';

const firstParagraphText = ({ json }) => {
  if (!json) return;

  const { blocks } = json;

  if (!blocks) return;

  const firstParagraphBlock = blocks.find(({ type }) => type === 'paragraph');

  if (!firstParagraphBlock) return;

  const { data } = firstParagraphBlock;
  if (!data) return;

  const { text } = data;

  return text;
};

const PostPage = ({ location, pageContext }) => {
  const pageType = pageTypes.POST;

  const computeGameName = post => {
    if (post) {
      return post.games && post.games.length > 0 && post.games[0].name;
    }
  };

  const computeCrumbs = gameName => {
    const crumbs = gameName
      ? [
          { text: `${gameName} Wiki`, url: routes.GameHub(gameName), hide: true },
          { text: `${gameName} News and Guides`, url: routes.PostHub(gameName) }
        ]
      : [{ text: 'News and Guides', url: routes.PostHub() }];
    return crumbs;
  };

  const computePageStructuredData = post => {
    if (post) {
      return {
        '@type': `NewsArticle`,
        headline: post.title,
        datePublished: post.publishedDate || post.createdAt,
        image: post.headerImage && [post.headerImage.url],
        author: {
          '@type': 'Organization',
          name: siteName
        }
      };
    }
  };

  const computePageTitle = post => {
    if (post) {
      const postGameName = post.games && post.games.length !== 0 && post.games[0].name;
      return postGameName
        ? `${post.title} | ${postGameName} | ${siteName}`
        : `${post.title} | ${siteName}`;
    }
    return `Loading`;
  };

  const computePageDescription = post => {
    let desc;

    if (post) {
      if (post.seoDescription) desc = post.seoDescription;

      if (post.content) {
        desc = removeMd(post.content);
      }

      if (post.contentJSON) {
        desc = firstParagraphText({ json: post.contentJSON });
      }
    }

    return desc && desc.substr(0, 160);
  };

  const relatedFeed = pageContext && pageContext.relatedFeed;

  const [gameName, setGameName] = useState(computeGameName(pageContext.post));
  const [post, setPost] = useState(pageContext.post);
  const [loadError, setLoadError] = useState(false);
  const [pageTitle, setPageTitle] = useState(computePageTitle(pageContext.post));
  const [headerImage, setHeaderImage] = useState(pageContext.post && pageContext.post.headerImage);
  const [pageDescription, setPageDescription] = useState(computePageDescription(pageContext.post));
  const [pageStructuredData, setPageStructuredDate] = useState(computePageStructuredData(post));
  const [crumbs, setCrumbs] = useState(computeCrumbs(gameName));

  useEffect(() => {
    const retrievePost = async () => {
      const permalink = location.pathname;
      const retrievedPost = await getPostByPermalink({ permalink });
      if (retrievedPost) {
        setPost(retrievedPost);
      } else {
        setLoadError(true);
      }
    };
    if (!post) {
      retrievePost();
    }
    if (post) {
      setHeaderImage(post && post.headerImage);
      setPageStructuredDate(computePageStructuredData(post));
      setPageTitle(computePageTitle(post));
      setPageDescription(computePageDescription(post));
      const gName = computeGameName(post);
      setGameName(gName);
      setCrumbs(computeCrumbs(gName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  return (
    <Layout
      location={location}
      gameName={gameName}
      title={pageTitle}
      description={pageDescription}
      shareImageURI={headerImage && headerImage.url}
      pageType={pageType}
      pageStructuredData={pageStructuredData}
    >
      {headerImage && (
        <ResponsiveImage>
          <HeaderImage src={headerImage.url} />
        </ResponsiveImage>
      )}
      <AdMobileAnchor placementId={'gh-anchor-' + pageType +'-bottom'} anchorType='bottom' offset='47' />
      <GameContent largeHeadline headerSolidBackground pop noHeader crumbs={crumbs}>
        <TwoColumn>
          <MainColumn>
            <AdMobileBanner placementId={"gh-mobilebanner-" + pageType + "-top"} />
            <AdLeaderboard placementId={"gh-leaderboard-" + pageType + "-top"} />
            <Post post={post} />
            {post ? (
              <>
                <Section solidBackground flushContent>
                  <AdBox placementId={"gh-box-" + pageType + "-end"} />
                </Section>
              </>
            ) : (
              <LoadingIndicator>
                {!loadError ? `Loading Guide` : `Something went wrong loading guide`}
              </LoadingIndicator>
            )}
          </MainColumn>
          <SecondaryColumn>
            <Section solidBackground>
              <ContentFeed feed={relatedFeed} pageContentID={post && post.id} />
            </Section>
            <AdVideo placementId={'gh-video-' + pageType + '-floating'} />
          </SecondaryColumn>
        </TwoColumn>
      </GameContent>
      <Footer />
    </Layout>
  );
};

export default PostPage;
